import { motion } from "framer-motion";

/**
 * KOTCHA™ — the signature West African bath net.
 * Renders as a long, hanging tubular mesh with diamond open-weave.
 * SVG-native; matte, low-contrast, no gloss.
 *
 * Presentation variants:
 *   "hanging"  — draped from a bar (default, tall)
 *   "rolled"   — a short cylindrical roll (for grids / rows)
 */
export default function KorvNet({
  color = "#3A2530",
  variant = "hanging",     // "hanging" | "rolled"
  label = "KOTCHA™",
  colorLabel,
  className = "",
  animate = true,
}) {
  const uid = (color + variant).replace(/[^a-z0-9]/gi, "");
  const dark = isDark(color);
  const strand = darken(color, isDark(color) ? -18 : -32);
  const light  = darken(color, isDark(color) ? 22 : 10);
  const bandColor = "#141212";
  const bandInk = "#EDE6D8";

  if (variant === "rolled") return <KorvNetRoll {...{ color, uid, strand, light, label, colorLabel, className, animate }} />;

  return (
    <motion.svg
      viewBox="0 0 240 640"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      initial={animate ? { y: 30, opacity: 0 } : false}
      whileInView={animate ? { y: 0, opacity: 1 } : false}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.6, ease: [0.2, 0.7, 0.1, 1] }}
      aria-hidden="true"
    >
      <defs>
        {/* Open-weave diamond pattern — the KOTCHA signature */}
        <pattern id={`weave-${uid}`} x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="14" height="14" fill={color} />
          {/* horizontal strand */}
          <rect x="0" y="6" width="14" height="2" fill={strand} />
          <rect x="0" y="6.3" width="14" height="0.5" fill={light} opacity="0.55" />
          {/* vertical strand */}
          <rect x="6" y="0" width="2" height="14" fill={strand} />
          <rect x="6.3" y="0" width="0.5" height="14" fill={light} opacity="0.55" />
        </pattern>
        {/* Body volumetric shading */}
        <linearGradient id={`shade-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(0,0,0,0.35)" />
          <stop offset="0.18" stopColor="rgba(0,0,0,0.05)" />
          <stop offset="0.5" stopColor="rgba(255,255,255,0.06)" />
          <stop offset="0.82" stopColor="rgba(0,0,0,0.05)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.35)" />
        </linearGradient>
        <linearGradient id={`vert-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(0,0,0,0.5)" />
          <stop offset="0.14" stopColor="rgba(0,0,0,0.05)" />
          <stop offset="0.85" stopColor="rgba(0,0,0,0)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.4)" />
        </linearGradient>
        {/* Ground shadow */}
        <radialGradient id={`ground-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(20,18,17,0.42)" />
          <stop offset="0.55" stopColor="rgba(20,18,17,0.16)" />
          <stop offset="1" stopColor="rgba(20,18,17,0)" />
        </radialGradient>
        <filter id={`sblur-${uid}`}>
          <feGaussianBlur stdDeviation="4.5" />
        </filter>

        {/* Body shape — a long draped tube, slightly wavy at the bottom */}
        <clipPath id={`body-${uid}`}>
          <path d="
            M 78 30
            C 74 60, 70 120, 68 220
            C 66 300, 64 380, 70 480
            C 72 520, 76 560, 82 590
            C 100 605, 140 605, 158 590
            C 164 560, 168 520, 170 480
            C 176 380, 174 300, 172 220
            C 170 120, 166 60, 162 30
            Z" />
        </clipPath>
      </defs>

      {/* Ground shadow */}
      <ellipse cx="120" cy="620" rx="70" ry="8"
        fill={`url(#ground-${uid})`} filter={`url(#sblur-${uid})`} />

      {/* Hanging bar */}
      <rect x="20" y="18" width="200" height="4" fill="#141212" opacity="0.8" rx="1" />
      <circle cx="24" cy="20" r="4" fill="#141212" />
      <circle cx="216" cy="20" r="4" fill="#141212" />

      {/* Top loop hanging from bar */}
      <path d="M 92 20 Q 92 40, 96 46 L 144 46 Q 148 40, 148 20"
        fill="none" stroke={strand} strokeWidth="3" />
      <path d="M 92 20 Q 92 40, 96 46 L 144 46 Q 148 40, 148 20"
        fill="none" stroke={light} strokeWidth="0.6" opacity="0.6" />

      {/* The NET body — filled with weave pattern */}
      <g clipPath={`url(#body-${uid})`}>
        <rect x="0" y="30" width="240" height="580" fill={`url(#weave-${uid})`} />
        {/* Shading overlays */}
        <rect x="0" y="30" width="240" height="580" fill={`url(#shade-${uid})`} opacity="0.9" />
        <rect x="0" y="30" width="240" height="580" fill={`url(#vert-${uid})`} opacity="0.85" />
      </g>

      {/* Contour outline (thin) */}
      <path d="
        M 78 30
        C 74 60, 70 120, 68 220
        C 66 300, 64 380, 70 480
        C 72 520, 76 560, 82 590
        C 100 605, 140 605, 158 590
        C 164 560, 168 520, 170 480
        C 176 380, 174 300, 172 220
        C 170 120, 166 60, 162 30
        Z"
        fill="none" stroke="rgba(0,0,0,0.20)" strokeWidth="0.6" />

      {/* Matte paper sleeve band — mid-body */}
      <g>
        <rect x="58" y="260" width="124" height="70" fill={bandColor} />
        <rect x="58" y="260" width="124" height="70" fill="rgba(0,0,0,0.15)" />
        <rect x="58" y="264" width="124" height="2" fill="rgba(255,255,255,0.08)" />
        <text
          x="120" y="290"
          textAnchor="middle" fill={bandInk}
          fontFamily="Fraunces, serif" fontWeight="500" fontSize="15"
          letterSpacing="2"
          style={{ fontVariationSettings: "'opsz' 144" }}
        >
          {label}
        </text>
        <text
          x="120" y="308"
          textAnchor="middle" fill="rgba(237,230,216,0.55)"
          fontFamily="'Inter Tight', sans-serif" fontSize="6" letterSpacing="2.4"
        >
          AFRICAN BATH NET
        </text>
        {colorLabel && (
          <text
            x="120" y="322"
            textAnchor="middle" fill="rgba(237,230,216,0.42)"
            fontFamily="'Inter Tight', sans-serif" fontSize="5.5" letterSpacing="2"
          >
            {colorLabel.toUpperCase()}
          </text>
        )}
      </g>
    </motion.svg>
  );
}

function KorvNetRoll({ color, uid, strand, light, label, colorLabel, className, animate }) {
  const bandColor = "#141212";
  const bandInk = "#EDE6D8";
  return (
    <motion.svg
      viewBox="0 0 200 340"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      initial={animate ? { y: 20, opacity: 0 } : false}
      whileInView={animate ? { y: 0, opacity: 1 } : false}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.4, ease: [0.2, 0.7, 0.1, 1] }}
      aria-hidden="true"
    >
      <defs>
        <pattern id={`rweave-${uid}`} x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="12" height="12" fill={color} />
          <rect x="0" y="5" width="12" height="1.8" fill={strand} />
          <rect x="5" y="0" width="1.8" height="12" fill={strand} />
        </pattern>
        <linearGradient id={`rshade-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(0,0,0,0.32)" />
          <stop offset="0.5" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.32)" />
        </linearGradient>
        <radialGradient id={`rground-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(20,18,17,0.42)" />
          <stop offset="1" stopColor="rgba(20,18,17,0)" />
        </radialGradient>
        <clipPath id={`rbody-${uid}`}>
          <rect x="40" y="30" width="120" height="270" rx="12" />
        </clipPath>
      </defs>

      <ellipse cx="100" cy="310" rx="60" ry="6" fill={`url(#rground-${uid})`} />

      <g clipPath={`url(#rbody-${uid})`}>
        <rect x="40" y="30" width="120" height="270" fill={`url(#rweave-${uid})`} />
        <rect x="40" y="30" width="120" height="270" fill={`url(#rshade-${uid})`} />
      </g>
      <rect x="40" y="30" width="120" height="270" rx="12" fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth="0.6" />

      {/* Matte paper band */}
      <g>
        <rect x="40" y="160" width="120" height="52" fill={bandColor} />
        <rect x="40" y="164" width="120" height="1.6" fill="rgba(255,255,255,0.08)" />
        <text x="100" y="182" textAnchor="middle" fill={bandInk}
          fontFamily="Fraunces, serif" fontWeight="500" fontSize="12" letterSpacing="1.6">
          {label}
        </text>
        <text x="100" y="196" textAnchor="middle" fill="rgba(237,230,216,0.55)"
          fontFamily="'Inter Tight', sans-serif" fontSize="5.5" letterSpacing="2">
          AFRICAN BATH NET
        </text>
        {colorLabel && (
          <text x="100" y="208" textAnchor="middle" fill="rgba(237,230,216,0.4)"
            fontFamily="'Inter Tight', sans-serif" fontSize="5" letterSpacing="2">
            {colorLabel.toUpperCase()}
          </text>
        )}
      </g>
    </motion.svg>
  );
}

function isDark(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
}
function darken(hex, percent) {
  const c = hex.replace("#", "");
  let r = parseInt(c.slice(0, 2), 16);
  let g = parseInt(c.slice(2, 4), 16);
  let b = parseInt(c.slice(4, 6), 16);
  const f = percent / 100;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * f)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * f)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * f)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}
